import React, { Component, ReactNode } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
  showErrorDetails?: boolean;
  context?: string; // Context where the error occurred (e.g., "ResidentList", "DataTable")
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: any;
  retryCount: number;
}

/**
 * Wrap screens/sections with AdminErrorBoundary to catch unexpected exceptions so the app doesn’t crash.
 */

export class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Enhanced error logging for admin context
    const context = this.props.context || 'AdminApp';
    
    console.group(`🔴 [AdminErrorBoundary] Error in ${context}`);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Retry Count:', this.state.retryCount);
    console.groupEnd();

    // Store error info for display
    this.setState({ errorInfo });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send error to crash reporting service
    // Example: Crashlytics.recordError(error);
  }

  handleRetry = () => {
    this.setState(prevState => ({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    // Reset retry count and error state
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: 0 
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, retryCount } = this.state;
      const { context, showErrorDetails = __DEV__ } = this.props;

      return (
        <View style={styles.container}>
          <ScrollView 
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* Error Icon */}
            <View style={styles.iconContainer}>
              <SVG_COMPONENTS.X_CIRCLE 
                width={48} 
                height={48} 
                color={ADMIN_COLORS.ERROR} 
              />
            </View>

            {/* Error Title */}
            <Text style={styles.title}>
              {context ? `${context} Error` : 'Something went wrong'}
            </Text>

            {/* Error Message */}
            <Text style={styles.message}>
              We encountered an unexpected error. This has been logged and our team will investigate.
            </Text>

            {/* Retry Information */}
            {retryCount > 0 && (
              <Text style={styles.retryInfo}>
                Retry attempts: {retryCount}
              </Text>
            )}

            {/* Action Buttons */}
            <View style={styles.buttonContainer}>
              <TouchableOpacity 
                style={[styles.button, styles.primaryButton]} 
                onPress={this.handleRetry}
              >
                <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color="#FFFFFF" />
                <Text style={styles.primaryButtonText}>Try Again</Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.button, styles.secondaryButton]} 
                onPress={this.handleReload}
              >
                <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color={ADMIN_COLORS.PRIMARY} />
                <Text style={styles.secondaryButtonText}>Reload</Text>
              </TouchableOpacity>
            </View>

            {/* Error Details (Development Only) */}
            {showErrorDetails && error && (
              <View style={styles.errorDetailsContainer}>
                <Text style={styles.errorDetailsTitle}>Error Details (Dev Only)</Text>
                
                <View style={styles.errorDetail}>
                  <Text style={styles.errorDetailLabel}>Error Message:</Text>
                  <Text style={styles.errorDetailText}>{error.message}</Text>
                </View>

                {error.stack && (
                  <View style={styles.errorDetail}>
                    <Text style={styles.errorDetailLabel}>Stack Trace:</Text>
                    <ScrollView style={styles.stackTraceContainer} horizontal>
                      <Text style={styles.stackTraceText}>{error.stack}</Text>
                    </ScrollView>
                  </View>
                )}

                {errorInfo?.componentStack && (
                  <View style={styles.errorDetail}>
                    <Text style={styles.errorDetailLabel}>Component Stack:</Text>
                    <ScrollView style={styles.stackTraceContainer}>
                      <Text style={styles.stackTraceText}>{errorInfo.componentStack}</Text>
                    </ScrollView>
                  </View>
                )}
              </View>
            )}
          </ScrollView>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  iconContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 50,
    backgroundColor: `${ADMIN_COLORS.ERROR}20`, // 20% opacity
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 8,
    lineHeight: 22,
    maxWidth: 300,
  },
  retryInfo: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    marginBottom: 24,
    fontStyle: 'italic',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 32,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
    minWidth: 120,
    justifyContent: 'center',
  },
  primaryButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButtonText: {
    color: ADMIN_COLORS.PRIMARY,
    fontSize: 16,
    fontWeight: '600',
  },
  errorDetailsContainer: {
    width: '100%',
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
  },
  errorDetailsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 12,
  },
  errorDetail: {
    marginBottom: 12,
  },
  errorDetailLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 4,
  },
  errorDetailText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    fontFamily: 'monospace',
  },
  stackTraceContainer: {
    maxHeight: 150,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    padding: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  stackTraceText: {
    fontSize: 10,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    fontFamily: 'monospace',
    lineHeight: 14,
  },
});
